import type { ErrorCodeMapperEntry } from "./errorCodes/types.js";
import { legacy1000Errors } from "./errorCodes/legacy1000.js";
import { orgStructureErrors } from "./errorCodes/orgStructure.js";
import { organisationErrors } from "./errorCodes/organisation.js";
import { accountErrors } from "./errorCodes/account.js";
import { rulesErrors } from "./errorCodes/rules.js";
import { sharedErrors } from "./errorCodes/shared.js";
import { schedulesErrors } from "./errorCodes/schedules.js";
import { issuesErrors } from "./errorCodes/issues.js";
import { selectionSetErrors } from "./errorCodes/selectionSet.js";
import { jiraErrors } from "./errorCodes/jira.js";
import { billingErrors } from "./errorCodes/billing.js";
import { formsErrors } from "./errorCodes/forms.js";
import { commentsErrors } from "./errorCodes/comments.js";
import { tasksErrors } from "./errorCodes/tasks.js";
import { contractsErrors } from "./errorCodes/contracts.js";
import { reviewsErrors } from "./errorCodes/reviews.js";
import { projectsErrors } from "./errorCodes/projects.js";
import { portalChatErrors } from "./errorCodes/portalChat.js";
import { portalErrors } from "./errorCodes/portal.js";
import { externalLinksErrors } from "./errorCodes/externalLinks.js";
import { resourcesErrors } from "./errorCodes/resources.js";
import { filesErrors } from "./errorCodes/files.js";
import { takeoffErrors } from "./errorCodes/takeoff.js";
import { calendarErrors } from "./errorCodes/calendar.js";
import { fileSystemErrors } from "./errorCodes/fileSystem.js";
import { fileContentErrors } from "./errorCodes/fileContent.js";
import { spacesErrors } from "./errorCodes/spaces.js";
import { mailboxErrors } from "./errorCodes/mailbox.js";
import { groupsErrors } from "./errorCodes/groups.js";
import { measurementsErrors } from "./errorCodes/measurements.js";
import { foldersErrors } from "./errorCodes/folders.js";
import { chatErrors } from "./errorCodes/chat.js";
import { clashErrors } from "./errorCodes/clash.js";
import { queriesErrors } from "./errorCodes/queries.js";
import { viewsErrors } from "./errorCodes/views.js";
import { assetsErrors } from "./errorCodes/assets.js";
import { pdfExportErrors } from "./errorCodes/pdfExport.js";
import { cad2pdfErrors } from "./errorCodes/cad2pdf.js";
import { transmittalErrors } from "./errorCodes/transmittal.js";
import { projectExternalErrors } from "./errorCodes/projectExternal.js";
import { notificationsErrors } from "./errorCodes/notifications.js";
import { layoutViewErrors } from "./errorCodes/layoutView.js";
import { statusErrors } from "./errorCodes/status.js";
import { currencyErrors } from "./errorCodes/currency.js";
import { escrowErrors } from "./errorCodes/escrow.js";
import { miscErrors } from "./errorCodes/misc.js";

export type { ErrorCodeMapperEntry } from "./errorCodes/types.js";

/**
 * The single mapper, merged from one file per module under `errorCodes/`. Each of those files owns
 * exactly one thousand-block, so a duplicate NUMBER can only occur inside a single file.
 *
 * ⚠️ A duplicate NAME across two module files is NOT a compile error — the later spread silently
 * wins. That is the one protection lost by splitting (a single object literal rejects duplicate
 * keys with TS1117). Names are module-prefixed to keep it unlikely.
 */
export const errorCodeMapper = {
  ...legacy1000Errors,
  ...orgStructureErrors,
  ...organisationErrors,
  ...accountErrors,
  ...rulesErrors,
  ...sharedErrors,
  ...schedulesErrors,
  ...issuesErrors,
  ...selectionSetErrors,
  ...jiraErrors,
  ...billingErrors,
  ...formsErrors,
  ...commentsErrors,
  ...tasksErrors,
  ...contractsErrors,
  ...reviewsErrors,
  ...projectsErrors,
  ...portalChatErrors,
  ...portalErrors,
  ...externalLinksErrors,
  ...resourcesErrors,
  ...filesErrors,
  ...takeoffErrors,
  ...calendarErrors,
  ...fileSystemErrors,
  ...fileContentErrors,
  ...spacesErrors,
  ...mailboxErrors,
  ...groupsErrors,
  ...measurementsErrors,
  ...foldersErrors,
  ...chatErrors,
  ...clashErrors,
  ...queriesErrors,
  ...viewsErrors,
  ...assetsErrors,
  ...pdfExportErrors,
  ...cad2pdfErrors,
  ...transmittalErrors,
  ...projectExternalErrors,
  ...notificationsErrors,
  ...layoutViewErrors,
  ...statusErrors,
  ...currencyErrors,
  ...escrowErrors,
  ...miscErrors,
};

/**
 * Name -> number, DERIVED from the mapper so a code is declared exactly once (in its module file).
 * The mapped-type assertion is load-bearing: `Object.fromEntries` alone returns an index signature,
 * which would make `errorCodes.TYPO` compile. This keeps every read checked against a real name.
 */
export const errorCodes = Object.fromEntries(
  Object.entries(errorCodeMapper).map(([name, entry]) => [name, entry.errorCode]),
) as { [K in keyof typeof errorCodeMapper]: (typeof errorCodeMapper)[K]["errorCode"] };

/**
 * The numeric errorCode is the only thing that actually crosses the wire — a response never carries
 * the semantic key errorCodeMapper is written under — so every lookup by code goes through this
 * reverse index rather than a linear `Object.values().find()` at call time.
 */
const errorCodeToEntry = new Map<number, ErrorCodeMapperEntry>(
  Object.values(errorCodeMapper).map((entry) => [entry.errorCode, entry]),
);

/** Looks up a mapper entry by the numeric `errorCode` a response actually carries. */
export function getEntryByErrorCode(errorCode: number): ErrorCodeMapperEntry | undefined {
  return errorCodeToEntry.get(errorCode);
}

/**
 * Picks the entry's RAW template — singular or, when `params.count` is a number greater than 1 and
 * the entry declares `errorMessagePlural`, the plural sentence — WITHOUT interpolating any
 * `{{param}}` placeholders in it.
 *
 * This is deliberately left uninterpolated: this app's i18next convention is that the untranslated
 * English sentence, placeholders and all, IS the translation lookup key (see en.json). A frontend
 * component therefore calls `t(getErrorMessageTemplate(entry, params), params)` — `t()` performs the
 * catalog lookup AND the `{{param}}` interpolation together, in one step, exactly like every other
 * hardcoded `t("...", {...})` call site in this app. Interpolating here first would hand `t()` an
 * already-substituted string that no longer matches any catalog key, silently breaking translation
 * for every non-English locale.
 */
export function getErrorMessageTemplate(
  entry: ErrorCodeMapperEntry,
  params?: Record<string, string | number>,
): string {
  const count = params?.count;
  if (entry.errorMessagePlural != null && typeof count === "number" && count > 1) {
    return entry.errorMessagePlural;
  }
  return entry.errorMessage;
}

const PLACEHOLDER_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/**
 * Resolves an `errorCode` (+ optional `params`) to the final user-facing string.
 *
 * This is the ONE place error text is produced. `t` is INJECTED, never imported — the house
 * convention in this package (see jiraConstants.ts), and what lets a single implementation serve
 * both the React client and the Node server without `packages/helpers` ever depending on i18next.
 *
 *   web    -> getTranslatedErrorMessage(code, params, t)   translated + interpolated by t()
 *   server -> getTranslatedErrorMessage(code, params)      plain English, interpolated here
 *
 * ⚠️ Order matters. `getErrorMessageTemplate` picks singular/plural FIRST, then the RAW template —
 * placeholders and all — goes into `t()`, which does the catalog lookup and the `{{param}}`
 * interpolation together in one step. This app's i18next convention makes the untranslated English
 * sentence the lookup key, so interpolating before `t()` would hand it a string matching no key and
 * silently break translation in every non-English locale.
 *
 * Never throws — an errorCode with no entry falls back to the generic sentence, since this must stay
 * safe to call from a catch-all error path.
 */
export function getTranslatedErrorMessage(
  errorCode: number,
  params?: Record<string, string | number>,
  t?: (key: string, options?: Record<string, string | number>) => string,
): string {
  // No `t` (server logs, Sentry breadcrumbs, MCP tool callers): fall back to interpolation only, so
  // the caller still gets readable English. A placeholder with no matching param is left visible
  // (`{{name}}`) rather than rendered as the literal text "undefined".
  const translate =
    t ??
    ((msg: string, options?: Record<string, string | number>) =>
      msg.replace(PLACEHOLDER_PATTERN, (whole, name: string) => {
        const value = options?.[name];
        return value == null ? whole : String(value);
      }));

  const entry = getEntryByErrorCode(errorCode);
  if (!entry) return translate("Something went wrong", params);
  return translate(getErrorMessageTemplate(entry, params), params);
}
